import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/news';
import { useSEO } from '@/hooks/useSEO';

const FORM_ID = 'db7mfnfidngsrf4ehn3g';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db7mfnfidngsrf4ehn3g';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function SubmitPage() {
  useSEO({
    title: '我要爆料 - 911爆料网',
    description:
      '通过911爆料网投稿通道，提交你身边的社会热点与民生线索，我们会认真核实并跟进。',
    keywords: '我要爆料,投稿,线索,911爆料网,民生线索',
  });

  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [tips, setTips] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get('contact_alt') ?? '').trim();
    if (honeypot) {
      form.reset();
      setStatus('success');
      return;
    }

    const title = String(fd.get('title') ?? '').trim();
    const category = String(fd.get('category') ?? '').trim();
    const description = String(fd.get('description') ?? '').trim();

    if (!title || !category || !description) {
      setStatus('error');
      setErrorMessage('请填写必填项：爆料标题、所属分类和详细描述。');
      return;
    }

    const body = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === 'contact_alt') return;
      body.append(key, String(value));
    });

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        form.reset();
        setTips(title);
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(
          serverMsg || '提交失败，请稍后重试，或直接发送邮件至 tip@911news.example。',
        );
      }
    } catch {
      setStatus('error');
      setErrorMessage('网络异常，提交未成功，请检查网络后重试。');
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '我要爆料' }]} />

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <section className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h1 className="flex items-center gap-2.5 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500 text-primary-950">
                  <i className="ri-megaphone-line text-2xl"></i>
                </span>
                我要爆料
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                把你看到的社会热点、突发状况或民生问题告诉我们。请尽量描述清楚事件发生的时间、地点与经过，我们会认真核实与跟进。
              </p>

              {status === 'success' && (
                <div className="mt-5 flex items-start gap-3 rounded-lg border border-accent-300 bg-accent-50 p-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500 text-primary-950">
                    <i className="ri-check-line text-xl"></i>
                  </span>
                  <div>
                    <p className="font-heading text-sm font-bold text-foreground-950">
                      爆料已提交成功
                    </p>
                    <p className="mt-1 text-sm text-foreground-600">
                      {tips ? `「${tips}」` : '你的线索'}
                      已收到，感谢你提供的信息。编辑团队会尽快核实，请留意后续进展。
                    </p>
                  </div>
                </div>
              )}

              {status === 'error' && (
                <div className="mt-5 flex items-start gap-3 rounded-lg border border-secondary-300 bg-secondary-50 p-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-error-warning-line text-xl"></i>
                  </span>
                  <div>
                    <p className="font-heading text-sm font-bold text-foreground-950">
                      提交未成功
                    </p>
                    <p className="mt-1 text-sm text-foreground-600">{errorMessage}</p>
                  </div>
                </div>
              )}

              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div>
                  <label
                    htmlFor="title"
                    className="mb-1.5 block font-label text-sm font-medium text-foreground-800"
                  >
                    爆料标题 <span className="text-secondary-600">*</span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    required
                    maxLength={80}
                    placeholder="用一句话概括你要爆料的事情"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div>
                  <label
                    htmlFor="category"
                    className="mb-1.5 block font-label text-sm font-medium text-foreground-800"
                  >
                    所属分类 <span className="text-secondary-600">*</span>
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    defaultValue=""
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="" disabled>
                      请选择分类
                    </option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="description"
                    className="mb-1.5 block font-label text-sm font-medium text-foreground-800"
                  >
                    详细描述 <span className="text-secondary-600">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    maxLength={500}
                    rows={7}
                    placeholder="请描述事件的时间、地点、经过，以及你希望我们关注的重点（不超过500字）"
                    className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  />
                  <p className="mt-1 text-xs text-foreground-400">最多 500 字</p>
                </div>

                <div>
                  <label
                    htmlFor="contact"
                    className="mb-1.5 block font-label text-sm font-medium text-foreground-800"
                  >
                    联系方式（选填）
                  </label>
                  <input
                    id="contact"
                    name="contact"
                    type="text"
                    maxLength={60}
                    placeholder="手机号或邮箱，便于我们与你核实线索"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  />
                  <p className="mt-1 text-xs text-foreground-400">
                    我们会对你的联系方式严格保密
                  </p>
                </div>

                <div className="field-note-extra" aria-hidden="true">
                  <label htmlFor="contact_alt">备用联系方式</label>
                  <input
                    id="contact_alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
                    {status === 'submitting' ? '提交中…' : '提交爆料'}
                  </button>
                  <p className="text-xs text-foreground-500">
                    提交即表示你同意我们对线索进行核实与整理
                  </p>
                </div>
              </form>
            </div>
          </section>

          <aside className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-lightbulb-line text-primary-600"></i>
                投稿小贴士
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                <li className="flex gap-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  <span>尽量写清时间、地点、人物与经过，便于核实。</span>
                </li>
                <li className="flex gap-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  <span>如有图片或视频线索，可在联系后另行提供。</span>
                </li>
                <li className="flex gap-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  <span>请确保线索真实，避免传播未经核实的信息。</span>
                </li>
                <li className="flex gap-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  <span>联系方式仅用于核实，我们会严格保密。</span>
                </li>
              </ul>
            </section>

            <section className="rounded-lg bg-primary-900 p-5 text-background-100">
              <h2 className="font-heading text-lg font-bold text-background-50">
                想换个方式联系我们？
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-background-300">
                你也可以直接发送邮件，将线索与材料一并附上。
              </p>
              <a
                href="mailto:tip@911news.example"
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-background-50 px-4 py-2 text-sm font-semibold text-primary-800 transition-colors hover:bg-accent-400 hover:text-primary-950"
              >
                <i className="ri-mail-send-line"></i>
                tip@911news.example
              </a>
              <Link
                to="/about"
                className="mt-3 block text-sm text-accent-300 transition-colors hover:text-accent-200"
              >
                查看关于我们与常见问题 →
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </Layout>
  );
}